<div class="share-dialog">
  <h1 mat-dialog-title>Share project {{ data.projectId }}</h1>

  <div mat-dialog-content class="dialog-content">
    <div class="section">
      <h3 class="section-title">People with access</h3>

      @if (loading) {
        <div class="loading-section">
          <mat-progress-bar mode="indeterminate" class="loading-progress"></mat-progress-bar>
          <div class="loading-message">Loading members...</div>
        </div>
      }

      @if (error) {
        <div class="error-message">
          {{ error }}
          <button mat-button color="primary" (click)="retryLoad()">Retry</button>
        </div>
      }

      @if (!loading && !error) {
        <mat-list class="members-list">
          @for (member of members; track member.userId) {
            <mat-list-item class="member-item">
              <!-- Avatar: photo when available, initials circle otherwise -->
              <div matListItemAvatar class="member-avatar" [attr.title]="getMemberDisplayName(member)">
                @if (member.photoUrl) {
                  <img [src]="member.photoUrl" [alt]="''" referrerpolicy="no-referrer" class="member-avatar-img" />
                } @else {
                  <span class="member-initials">{{ getMemberInitials(member) }}</span>
                }
              </div>

              <!-- Display name as primary title -->
              <div matListItemTitle class="member-name">{{ getMemberDisplayName(member) }}</div>

              <!-- Email as subtitle -->
              <div matListItemLine class="member-email-line">{{ member.email }}</div>

              <!-- Role: static chip for non-manageable; dropdown chip for owners -->
              <div class="member-role">
                @if (!canManageUser(member)) {
                  <mat-chip-set class="role-chip">
                    <mat-chip [color]="getRoleColor(member.role)" selected>
                      <mat-icon class="role-icon-small">{{ getRoleIcon(member.role) }}</mat-icon>
                      {{ member.roleDisplayName }}
                    </mat-chip>
                  </mat-chip-set>
                }
                @if (canManageUser(member)) {
                  <div class="role-dropdown">
                    <mat-chip
                      [matMenuTriggerFor]="roleMenu"
                      [color]="getRoleColor(member.role)"
                      selected
                      class="role-chip-button"
                      [attr.aria-label]="'Change role for ' + getMemberDisplayName(member)"
                    >
                      <mat-icon class="role-icon-small">{{ getRoleIcon(member.role) }}</mat-icon>
                      <span>{{ member.roleDisplayName }}</span>
                      <mat-icon>arrow_drop_down</mat-icon>
                    </mat-chip>
                    <mat-menu #roleMenu="matMenu">
                      <button mat-menu-item (click)="onRemoveUser(member)" [disabled]="loading">
                        <mat-icon color="warn">person_remove</mat-icon>
                        <span>Remove access</span>
                      </button>
                      <mat-divider></mat-divider>
                      @for (role of availableRoles; track role) {
                        @if (member.role !== role.value) {
                          <button mat-menu-item (click)="onChangeRole(member, role.value)" [disabled]="loading">
                            <mat-icon>{{ getRoleIcon(role.value) }}</mat-icon>
                            <span>Change to {{ role.name }}</span>
                          </button>
                        }
                      }
                    </mat-menu>
                  </div>
                }
              </div>
            </mat-list-item>
          }
        </mat-list>

        @if (members.length === 0) {
          <div class="no-members-state">
            <mat-icon>group</mat-icon>
            <span>No members found</span>
          </div>
        }
      }
    </div>

    <mat-divider></mat-divider>

    <div class="section">
      <h3 class="section-title">Invite people</h3>

      <!-- Email input with chip display -->
      <div class="invite-container">
        <div class="email-field-container">
          <!-- Input state -->
          @if (!emailChip) {
            <mat-form-field class="email-field" appearance="outline">
              <mat-label>Enter email address</mat-label>
              <input matInput type="email" [formControl]="emailFormControl" (keydown)="onKeyDown($event)" />
              <!-- Angular Material automatic error handling -->
              @if (emailFormControl.hasError('email') && emailFormControl.touched) {
                <mat-error> Please enter a valid email address </mat-error>
              }
              @if (emailFormControl.hasError('emailExists')) {
                <mat-error> User is already a member of this project </mat-error>
              }
              @if (emailFormControl.hasError('allowlistError')) {
                <mat-error>
                  {{ emailFormControl.getError('allowlistError')?.message }}
                </mat-error>
              }
              @if (emailFormControl.hasError('serverError')) {
                <mat-error>
                  {{ emailFormControl.getError('serverError')?.message }}
                </mat-error>
              }
            </mat-form-field>
          }

          <!-- Chip state using real mat-form-field for consistent styling -->
          @if (emailChip) {
            <mat-form-field class="email-field chip-state" appearance="outline">
              <mat-label>Enter email address</mat-label>
              <!-- Hidden input bound to FormControl so mat-error can display -->
              <input
                matInput
                [formControl]="emailFormControl"
                readonly
                (keydown)="onKeyDown($event)"
                #chipInput
                type="text"
                autocomplete="off"
                spellcheck="false"
                style="
                  opacity: 0;
                  position: absolute;
                  width: 100%;
                  height: 100%;
                  top: 0;
                  left: 0;
                  z-index: 0;
                  background: transparent;
                  border: none;
                  outline: none;
                "
              />
              <!-- Chip display overlay -->
              <div class="chip-overlay">
                <mat-chip-set class="email-chip-set">
                  <mat-chip
                    class="email-chip"
                    [class.error-chip]="emailFormControl.invalid"
                    (removed)="removeEmailChip()"
                  >
                    {{ emailChip }}
                    <button matChipRemove>
                      <mat-icon>cancel</mat-icon>
                    </button>
                  </mat-chip>
                </mat-chip-set>
              </div>
              <!-- Show all error types in chip state -->
              @if (emailFormControl.hasError('emailExists')) {
                <mat-error> User is already a member of this project </mat-error>
              }
              @if (emailFormControl.hasError('allowlistError')) {
                <mat-error>
                  {{ emailFormControl.getError('allowlistError')?.message }}
                </mat-error>
              }
              @if (emailFormControl.hasError('serverError')) {
                <mat-error>
                  {{ emailFormControl.getError('serverError')?.message }}
                </mat-error>
              }
            </mat-form-field>
          }
        </div>

        <!-- Role selector -->
        <mat-form-field class="role-field" appearance="outline">
          <mat-label>Role</mat-label>
          <mat-select [(value)]="selectedRole" [disabled]="!isOwner || loading">
            @for (role of availableRoles; track role) {
              <mat-option [value]="role.value" [matTooltip]="role.description" matTooltipPosition="right">
                {{ role.name }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>

      <!-- Progress bar for async validation -->
      <div class="validation-progress-container">
        @if (isChipValidating) {
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        }
      </div>

      @if (permissionsLoaded && !isOwner) {
        <div class="permission-notice">
          <mat-icon>info</mat-icon>
          <span>Only project owners can manage access to this project.</span>
        </div>
      }
    </div>
  </div>

  <div mat-dialog-actions class="dialog-actions">
    <div class="left-actions">
      <button mat-button (click)="onCopyLink()" [disabled]="loading">
        <mat-icon>link</mat-icon>
        Copy link
      </button>
    </div>

    <div class="right-actions">
      <button mat-button (click)="onCancel()" [disabled]="loading">Cancel</button>

      @if (hasInvitationReady()) {
        <button
          mat-raised-button
          color="primary"
          (click)="onShareInvitation()"
          [disabled]="!isOwner || loading || !hasInvitationReady()"
        >
          <mat-icon>person_add</mat-icon>
          Share
        </button>
      }

      @if (!hasInvitationReady()) {
        <button mat-raised-button color="primary" (click)="dialogRef.close()" [disabled]="loading">Done</button>
      }
    </div>
  </div>
</div>
